import React, { useState, useEffect } from 'react';
import { DollarSign, FileText, Calendar } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { crumbsFor } from '../navigation/modules';
import api from '../services/api';
import { useAmoebaConfig } from '../hooks/useAmoebaConfig';

const MySalary = () => {
    // The tenant's real currency, from the public /auth/config endpoint. This page
    // used to hardcode INR, so employees of a USD/EUR tenant saw ₹ amounts.
    const { config } = useAmoebaConfig();
    const formatCurrency = (val) =>
        new Intl.NumberFormat('en-IN', { style: 'currency', currency: config.baseCurrency || 'INR' })
            .format(Number(val) || 0);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get('/payroll/my-history')
            .then(res => setHistory(Array.isArray(res.data) ? res.data : []))
            // Previously console.error only, so a 403 (payroll module disabled) was
            // indistinguishable from "you have no payslips yet". api.js already
            // attaches friendlyMessage for the module-disabled case.
            .catch(err => setError(err.friendlyMessage || err.response?.data?.error || 'Could not load your salary history.'))
            .finally(() => setLoading(false));
    }, []);

    const viewSlip = (month, year) => {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        if (!stored.id) { alert('Your session has expired. Please sign in again.'); return; }
        api.get('/payroll/slip', { params: { userId: stored.id, month, year }, responseType: 'text' })
            .then(res => {
                const win = window.open('', '_blank');
                // window.open returns null when a popup blocker intervenes.
                if (!win) { alert('Please allow pop-ups for this site to view your payslip.'); return; }
                win.document.write(res.data);
                win.document.close();
            })
            .catch(() => alert('Failed to open payslip.'));
    };

    return (
        <div className="container section">
            <PageHeader crumbs={crumbsFor('payslips')} />

            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <DollarSign style={{ marginRight: '0.75rem', color: '#2563eb' }} /> My Salary History
            </h1>

            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid #e5e7eb' }}>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Month</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Payable Days</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Monthly CTC</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Earned Gross</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Deductions</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>LWP days</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Net Pay</th>
                                <th style={{ padding: '1rem', fontWeight: '600', textAlign: 'right' }}>Slip</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center' }}>Loading...</td></tr>
                            ) : error ? (
                                <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#b91c1c' }}>{error}</td></tr>
                            ) : history.length === 0 ? (
                                <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center' }}>No salary history found.</td></tr>
                            ) : (
                                history.map((r) => (
                                    <tr key={`${r.year}-${r.month}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <Calendar size={16} />
                                            {new Date(0, r.month - 1).toLocaleString('default', { month: 'long' })} {r.year}
                                        </td>
                                        <td style={{ padding: '1rem' }}>{r.payableDays} / {r.daysInMonth}</td>
                                        <td style={{ padding: '1rem', color: 'var(--text-muted)' }}>{formatCurrency(r.grossSalary)}</td>
                                        {/* Earned Gross − Deductions = Net Pay. Showing only CTC next to
                                            Net left an unexplained gap whenever there were unpaid days. */}
                                        <td style={{ padding: '1rem' }}>{formatCurrency(r.earnedGross)}</td>
                                        <td style={{ padding: '1rem', color: '#b91c1c' }}>-{formatCurrency(r.taxDeductions)}</td>
                                        <td style={{ padding: '1rem', color: r.unpaidDaysCount > 0 ? '#b91c1c' : 'inherit' }}>{r.unpaidDaysCount}</td>
                                        <td style={{ padding: '1rem', fontWeight: 'bold' }}>{formatCurrency(r.netPay)}</td>
                                        <td style={{ padding: '1rem', textAlign: 'right' }}>
                                            <button
                                                onClick={() => viewSlip(r.month, r.year)}
                                                style={{ border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: '500' }}
                                            >
                                                View Slip
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default MySalary;
